// src/screens/s2/PensionLever.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 8 — Pension Lever.
// Top:    Contribution decision — available cash, monthly / annual inputs.
// Middle: CT saving (framed as "your cash, reduced bill" not "government
//         top-up"), with a three-way radio for what to do with the saving.
// Table:  Year-by-year to retirement — age, contribution, CT saved, net cost,
//         pension pot at growth assumption.
// Bottom: Freedom age + gap impact, before/after card pair with the delta
//         that this lever buys. "One new client funds this" callout.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import { useXeroData } from '../../hooks/useXeroData';
import { effectiveCTRate } from '../../taxConfig';
import { fvContributions } from '../../utils/planCalculations';
import { formatCurrency, formatClients, formatAge, formatPercent } from '../../utils/formatters';
import { useCommaInput } from '../../utils/useCommaInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

type CtReinvestOption = 'pension' | 'ef' | 'debt';

export default function PensionLever({ data, onUpdate }: Props) {
  const xero = useXeroData(data.xeroTenantId);

  // All hooks must be called unconditionally before any early-return branch —
  // otherwise React sees a different hook count across renders and throws
  // "Rendered more hooks than during the previous render", unmounting root.
  const s2Pension = data.s2PensionMonthly ?? 0;
  const pensionInput = useCommaInput(
    s2Pension,
    (v) => onUpdate({ s2PensionMonthly: Math.max(0, v) }),
    true,
  );

  if (xero.loading) {
    return <div className="p-6 max-w-6xl mx-auto text-sm text-gray-500">Loading pension lever data…</div>;
  }
  if (!xero.mapped || !xero.summary) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">Pension Lever</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {xero.error || 'This lever requires live Xero P&L to compute available cash and CT saving.'}
        </div>
      </div>
    );
  }

  const s = xero.summary!;

  // ── Available cash (approximation) ──────────────────────────────────────
  // Monthly post-CT profit less the commitments set in capital allocation
  // (dividends + CT reserve); the residual is the theoretical pension ceiling.
  const ctRate = effectiveCTRate(s.netProfit);
  const patMonthly = (s.netProfit * (1 - ctRate)) / 12;
  const immediateDeductions = (data.s2DividendMonthly ?? 0) + (data.s2CtReserveMonthly ?? 0);
  const availableForPension = Math.max(0, patMonthly - immediateDeductions);

  // ── Contribution decision (bound input) ─────────────────────────────────
  // s2Pension + pensionInput hoisted above; use their already-computed values.
  const annualContribution = s2Pension * 12;

  // ── CT saving ───────────────────────────────────────────────────────────
  // Employer pension is a deductible business expense — saves CT at the
  // effective rate that applies to the marginal pound of profit.
  const ctSavedAnnual = annualContribution * ctRate;
  const netCostAnnual = annualContribution - ctSavedAnnual;
  const reinvestRoute: CtReinvestOption = 'pension';    // radio state lives locally; adviser narrative only

  // ── Year-by-year projection ─────────────────────────────────────────────
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const growthRate = data.assumptionPensionGrowth || 0.08;

  const yearRows: Array<{ year: number; age: number; contribution: number; ctSaved: number; netCost: number; potValue: number }> = [];
  let pot = data.pensionBalance || 0;
  for (let i = 0; i < yearsToFreedom; i++) {
    const age = clientAge + i;
    pot = pot * (1 + growthRate) + annualContribution;
    yearRows.push({
      year: new Date().getFullYear() + i,
      age,
      contribution: annualContribution,
      ctSaved: ctSavedAnnual,
      netCost: netCostAnnual,
      potValue: Math.round(pot),
    });
  }
  const potAtFreedom = pot;

  // ── Gap impact ──────────────────────────────────────────────────────────
  // NPV of this lever at freedom age = fv of contributions + cumulative pot
  // contribution attributable to this lever. Simpler summary: pot attributable
  // to ONLY this contribution stream (ignoring existing balance).
  const potFromLever = fvContributions(s2Pension, growthRate * 100, yearsToFreedom);
  const gapBefore = data.s2GapBefore ?? data.gapAmount ?? 0;
  // Apply a conservative 4% drawdown rate to turn pot into annual income.
  const annualIncomeFromLever = potFromLever * 0.04;
  const gapAfter = Math.max(0, gapBefore - annualIncomeFromLever);

  // Before/after freedom age: no lever = move back, with lever = stays.
  // Approximation: without this contribution we lose potFromLever pot.
  // Convert the delta to years using a flat drawdown rate assumption.
  const ageDelta = gapBefore > 0 && annualIncomeFromLever > 0
    ? Math.min(yearsToFreedom, Math.round((annualIncomeFromLever / gapBefore) * yearsToFreedom))
    : 0;
  const ageBefore = freedomAge + ageDelta;
  const ageAfter = freedomAge;

  // ── "One new client funds this" ─────────────────────────────────────────
  const clientCount = data.businessClientCount || 0;
  const avgFee = clientCount > 0 ? s.revenue / clientCount : 0;
  const clientsNeeded = avgFee > 0 ? Math.max(0, netCostAnnual / avgFee) : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          Pension Lever
          {xero.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {xero.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          An employer pension contribution is a business expense. CT rate on that contribution stays in the business.
        </p>
      </div>

      {/* TOP — CONTRIBUTION DECISION */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Contribution decision</h3>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Available for pension</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(availableForPension)}/mo</div>
          <p className="text-xs text-gray-500 mt-2">PAT monthly less S2 dividends and CT reserve.</p>
        </div>
        <div className="rounded-2xl border-2 p-4" style={{ borderColor: '#10B981', backgroundColor: '#ECFDF5' }}>
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">Proposed employer contribution</div>
          <div className="flex items-baseline gap-1">
            <span className="text-emerald-700">£</span>
            <input
              {...pensionInput}
              placeholder="0"
              className="text-2xl font-bold text-emerald-700 bg-transparent border-none p-0 focus:outline-none w-full"
            />
            <span className="text-xs text-emerald-700">/mo</span>
          </div>
          <p className="text-xs text-emerald-700 mt-2">Paid by the company direct to pension provider.</p>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Annual contribution</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(annualContribution)}</div>
          <p className="text-xs text-gray-500 mt-2">
            {s2Pension > availableForPension && availableForPension > 0
              ? 'Above the calculated headroom — confirm this is sustainable.'
              : '£' + Math.round(s2Pension) + ' × 12'}
          </p>
        </div>
      </div>

      {/* MIDDLE — CT SAVING */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">CT saving</h3>
      <div className="rounded-2xl p-5 mb-6" style={{ background: 'linear-gradient(135deg, #1B2B4B 0%, #2a3f6b 100%)', color: '#FFFFFF' }}>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Gross cost to business</div>
            <div className="text-2xl font-bold">{formatCurrency(annualContribution)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">CT saved @ {formatPercent(ctRate * 100)}</div>
            <div className="text-2xl font-bold" style={{ color: '#C9A84C' }}>
              {formatCurrency(ctSavedAnnual)}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 mb-1">Net cost</div>
            <div className="text-2xl font-bold">{formatCurrency(netCostAnnual)}</div>
          </div>
        </div>
        <p className="text-sm mt-4 opacity-90 leading-relaxed">
          This contribution reduces your CT bill by <strong style={{ color: '#C9A84C' }}>{formatCurrency(ctSavedAnnual)}</strong>
          {' '}— that cash stays in the business.
        </p>
      </div>

      {/* CT REINVEST OPTIONS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
          Use the CT saving for
        </div>
        <div className="grid grid-cols-3 gap-3">
          {([
            { key: 'pension', title: 'More pension', body: 'Loop it back into next year\'s contribution to compound faster.' },
            { key: 'ef', title: 'EF acceleration', body: 'Fund the business EF target in fewer months.' },
            { key: 'debt', title: 'Debt clearance', body: 'Pay down historic CT / VAT / PAYE sooner.' },
          ] as Array<{ key: CtReinvestOption; title: string; body: string }>).map((opt) => (
            <div
              key={opt.key}
              className={`rounded-xl border-2 p-3 cursor-pointer transition-colors ${reinvestRoute === opt.key ? 'border-ma-navy bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-start gap-2">
                <input type="radio" checked={reinvestRoute === opt.key} readOnly className="mt-1" />
                <div>
                  <div className="text-sm font-bold text-ma-navy">{opt.title}</div>
                  <p className="text-xs text-gray-600 mt-1">{opt.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* YEAR-BY-YEAR TABLE */}
      {yearRows.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6 overflow-hidden">
          <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
            Projected to freedom age {formatAge(freedomAge)}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="text-left py-2 pr-3">Year</th>
                  <th className="text-left py-2 pr-3">Age</th>
                  <th className="text-right py-2 pr-3">Contribution</th>
                  <th className="text-right py-2 pr-3">CT saved</th>
                  <th className="text-right py-2 pr-3">Net cost</th>
                  <th className="text-right py-2">Pension pot</th>
                </tr>
              </thead>
              <tbody>
                {yearRows.map((row) => (
                  <tr key={row.year} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 pr-3 text-gray-700">{row.year}</td>
                    <td className="py-2 pr-3 text-gray-700">{row.age}</td>
                    <td className="py-2 pr-3 text-right text-gray-700">{formatCurrency(row.contribution)}</td>
                    <td className="py-2 pr-3 text-right text-emerald-700 font-medium">{formatCurrency(row.ctSaved)}</td>
                    <td className="py-2 pr-3 text-right text-gray-700">{formatCurrency(row.netCost)}</td>
                    <td className="py-2 text-right font-semibold text-ma-navy">{formatCurrency(row.potValue)}</td>
                  </tr>
                ))}
                <tr className="font-bold text-ma-navy border-t-2 border-gray-200">
                  <td colSpan={2} className="py-2 pr-3">At {formatAge(freedomAge)}</td>
                  <td className="py-2 pr-3 text-right">{formatCurrency(annualContribution * yearsToFreedom)}</td>
                  <td className="py-2 pr-3 text-right" style={{ color: '#10B981' }}>{formatCurrency(ctSavedAnnual * yearsToFreedom)}</td>
                  <td className="py-2 pr-3 text-right">{formatCurrency(netCostAnnual * yearsToFreedom)}</td>
                  <td className="py-2 text-right" style={{ color: '#C9A84C' }}>{formatCurrency(potAtFreedom)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FREEDOM AGE + GAP IMPACT */}
      <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">Freedom age impact</h3>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Gap entering S2</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(gapBefore)}/yr</div>
          <p className="text-xs text-gray-500 mt-2">Unfunded annual retirement income need.</p>
        </div>
        <div className="rounded-2xl border-2 p-5" style={{ borderColor: '#10B981', backgroundColor: '#ECFDF5' }}>
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">This lever closes</div>
          <div className="text-2xl font-bold text-emerald-700">{formatCurrency(annualIncomeFromLever)}/yr</div>
          <p className="text-xs text-emerald-700 mt-2">
            {formatCurrency(potFromLever)} pot at freedom, 4% drawdown.
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Remaining gap</div>
          <div className="text-2xl font-bold text-ma-navy">{formatCurrency(gapAfter)}/yr</div>
          <p className="text-xs text-gray-500 mt-2">Still to close via growth levers.</p>
        </div>
      </div>

      {/* BEFORE / AFTER CARD PAIR */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Without this lever</div>
          <div className="text-4xl font-bold text-gray-600">Age {formatAge(ageBefore)}</div>
          <p className="text-xs text-gray-500 mt-2">Freedom age slips by {ageDelta} year{ageDelta === 1 ? '' : 's'}.</p>
        </div>
        <div className="rounded-2xl border-2 p-6 text-center" style={{ borderColor: '#C9A84C', backgroundColor: '#FFF9ED' }}>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#8B7A3E' }}>
            With this lever
          </div>
          <div className="text-4xl font-bold" style={{ color: '#C9A84C' }}>Age {formatAge(ageAfter)}</div>
          <p className="text-xs mt-2" style={{ color: '#8B7A3E' }}>
            → saves {ageDelta} year{ageDelta === 1 ? '' : 's'} to freedom.
          </p>
        </div>
      </div>

      {/* ONE NEW CLIENT CALLOUT */}
      {avgFee > 0 && clientsNeeded > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <p className="text-sm text-gray-700">
            <strong>{formatClients(clientsNeeded)} new client{formatClients(clientsNeeded) === 1 ? '' : 's'}</strong>
            {' '}at your average fee of <strong>{formatCurrency(avgFee)}</strong>
            {' '}funds this entire contribution — and delivers <strong style={{ color: '#C9A84C' }}>{formatCurrency(ctSavedAnnual)}</strong>
            {' '}of CT saving on top.
          </p>
        </div>
      )}
    </div>
  );
}
